import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeadset, 
  faTicketAlt, 
  faExchangeAlt,
  faUserCog,
  faCodeBranch,
  faComments,
  faArrowRight,
  faLaptopCode
} from '@fortawesome/free-solid-svg-icons';
import './Support.scss';

/**
 * Support component - Displays information about MET Connect®'s support services
 * 
 * @returns {JSX.Element} The Support component
 */
const Support = () => {

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /**
   * Handle click to open remote support portal
   */
  const handleRemoteSupportClick = () => {
    window.open('https://help.met-connect.com/', '_blank');
  };

  // Support offerings with their descriptions
  const supportOptions = [
    {
      icon: faHeadset,
      title: "Technical Support",
      description: "Our technical support team is available to assist you with any issues or questions you may have."
    },
    {
      icon: faTicketAlt,
      title: "Ticket System",
      description: "Submit and track support tickets through our online portal for efficient issue resolution."
    },
    {
      icon: faExchangeAlt,
      title: "Remote Assistance",
      description: "Get direct help from our engineers through secure remote assistance sessions."
    },
    {
      icon: faUserCog,
      title: "System Configuration",
      description: "Expert assistance setting up and configuring your MET Connect® services for optimal performance."
    },
    {
      icon: faCodeBranch,
      title: "System Updates",
      description: "Stay current with the latest features and security enhancements for your MET Connect® solutions."
    },
    {
      icon: faComments,
      title: "Consultation Services",
      description: "Schedule consultations with our experts for personalized guidance and solutions."
    }
  ];

  return (
    <div className="support-page">
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <Container className="hero-content">
          <Row className="align-items-center">
            <Col lg={7} className="hero-text">
              <h1 className="hero-title">
                MET Connect<sup>®</sup> Support Services
              </h1>
            </Col>
            <div className="vertical-divider-container">
              <div className="vertical-divider"></div>
            </div>
            <Col>
              <p className="hero-subtitle">
                Dedicated technical assistance for all MET Connect® products and services,
                with secure remote support available 24/7.
              </p>
              <div className="hero-features">
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faHeadset} className="feature-icon" />
                  <span>Expert Technical Support</span>
                </div>
                <div className="hero-feature">
                  <FontAwesomeIcon icon={faLaptopCode} className="feature-icon" />
                  <span>Remote Diagnostics</span>
                </div>
              </div>
              <div className="hero-buttons">
                <Button 
                  variant="primary" 
                  size="lg"
                  className="animated-btn"
                  onClick={handleRemoteSupportClick}
                >
                  <span className="animated-btn-content">
                    Access Remote Support
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </span>
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <section className="support-options-section">
        <Container>
          <h2 className="text-center mb-5">Support Services</h2>
          <Row>
            {supportOptions.map((option, index) => (
              <Col lg={6} xl={4} key={index} className="mb-4">
                <Card className="support-card h-100">
                  <Card.Body>
                    <div className="support-icon">
                      <FontAwesomeIcon icon={option.icon} />
                    </div>
                    <Card.Title>{option.title}</Card.Title>
                    <Card.Text>{option.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Support;